import React from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { FeatureGuard } from './FeatureGuard';

const SubscriptionTest: React.FC = () => {
  const { 
    subscriptionStatus, 
    currentPlan, 
    isLoading, 
    error,
    hasFeature,
    getCurrentUsage,
    getLimit,
    incrementUsage
  } = useSubscription();

  if (isLoading) {
    return (
      <Card className="w-full max-w-2xl">
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-red-600">Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!subscriptionStatus) {
    return (
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Subscription Status</CardTitle>
          <CardDescription>No subscription data available</CardDescription>
        </CardHeader>
        <CardContent>
          <p>Please check your authentication and try again.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
          <CardDescription>Your subscription details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-medium">Plan:</span>
            <Badge variant={currentPlan?.name === 'free' ? 'outline' : 'default'}>
              {currentPlan?.display_name || 'Unknown'}
            </Badge>
          </div>
          
          {currentPlan?.price_monthly !== undefined && currentPlan.price_monthly > 0 && (
            <div className="flex items-center justify-between">
              <span className="font-medium">Monthly Price:</span>
              <span>${currentPlan.price_monthly}/month</span>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <span className="font-medium">Status:</span>
            <Badge variant={subscriptionStatus.subscription.status === 'active' ? 'default' : 'destructive'}>
              {subscriptionStatus.subscription.status}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Usage */}
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Usage This Month</CardTitle>
          <CardDescription>Your current usage and limits</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span>Reviews Created:</span>
            <span>{getCurrentUsage('reviews_created')} / {getLimit('reviews_created') === -1 ? '∞' : getLimit('reviews_created')}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span>Storage Used:</span>
            <span>{getCurrentUsage('storage_used_gb')} / {getLimit('storage_used_gb') === -1 ? '∞' : getLimit('storage_used_gb')} GB</span>
          </div>
          
          <Button 
            onClick={() => incrementUsage('reviews_created')}
            variant="outline"
            size="sm"
          >
            Test: Increment Reviews
          </Button>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Feature Access</CardTitle>
          <CardDescription>Available features for your plan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscriptionStatus.features.map((feature) => (
            <div key={feature.feature} className="flex items-center justify-between">
              <span className="capitalize">{feature.feature.replace(/_/g, ' ')}:</span>
              <Badge variant={feature.isEnabled ? 'default' : 'secondary'}>
                {feature.isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Feature Tests */}
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Feature Tests</CardTitle>
          <CardDescription>Test feature guards</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FeatureGuard feature="reviews">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">✅ Reviews feature is available!</p>
            </div>
          </FeatureGuard>

          <FeatureGuard feature="advanced_analytics">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">📊 Advanced Analytics is available!</p>
            </div>
          </FeatureGuard>

          <FeatureGuard feature="slack_integration">
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-purple-800">💬 Slack Integration is available!</p>
            </div>
          </FeatureGuard>

          <FeatureGuard feature="custom_branding">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-orange-800">🎨 Custom Branding is available!</p>
            </div>
          </FeatureGuard>

          <FeatureGuard feature="api_access">
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-800">🔌 API Access is available!</p>
            </div>
          </FeatureGuard>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionTest; 